"use client"

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import dynamic from 'next/dynamic'
import Navbar from '@/lumora/components/layout/Navbar'

const CircuitNetwork  = dynamic(() => import('@/lumora/components/widgets/CircuitNetwork'),    { ssr: false })
const TextType        = dynamic(() => import('@/lumora/components/widgets/TextType'),          { ssr: false })
const LandingAnimations = dynamic(() => import('@/lumora/components/landing/LandingAnimations'), { ssr: false })
const FeaturedModels  = dynamic(() => import('@/lumora/components/landing/FeaturedModels'),    { ssr: false })
const LogoLoop        = dynamic(() => import('@/lumora/components/landing/LogoLoop'),          { ssr: false })
const AiTrendsChart   = dynamic(() => import('@/lumora/components/landing/AiTrendsChart'),     { ssr: false })
const TeamSection     = dynamic(() => import('@/lumora/components/landing/TeamSection'),       { ssr: false })
const Testimonials    = dynamic(() => import('@/lumora/components/landing/Testimonials'),      { ssr: false })
const FAQ             = dynamic(() => import('@/lumora/components/landing/FAQ'),               { ssr: false })
const LandingFooter   = dynamic(() => import('@/lumora/components/landing/LandingFooter'),     { ssr: false })

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [termsChecked, setTermsChecked] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  useEffect(() => {
    // globals.css locks html+body to height:100% for the chat app layout.
    // The login page is a long scrollable landing page, so we override that here.
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.height
    const prevBody = body.style.height
    html.style.height = 'auto'
    body.style.height = 'auto'
    return () => {
      html.style.height = prevHtml
      body.style.height = prevBody
    }
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    router.push('/chat')
  }

  return (
    <div style={{ width: '100vw', background: '#0D0B14' }}>
      <style>{`
        @media (max-width: 900px) {
          .lumora-hero { height: auto !important; min-height: 100vh; overflow: visible !important; padding-bottom: 48px; }
          .lumora-hero-row { flex-direction: column !important; height: auto !important; padding-top: 96px; gap: 40px; }
          .lumora-hero-brand { padding: 0 24px !important; }
          .lumora-hero-card { width: 100% !important; max-width: 440px; margin: 0 auto !important; box-sizing: border-box; }
        }
      `}</style>

      {/* ── Hero — viewport-locked on desktop, natural height on mobile ── */}
      <div className="lumora-hero" style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <CircuitNetwork style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.9 }} />
        {/* Dimming overlay — keeps text/card readable */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(13,11,20,0.68)', zIndex: 1, pointerEvents: 'none' }} />
        {/* Bottom fade — blends hero into the page background #0D0B14 */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 55%, #0D0B14 100%)', zIndex: 2, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 30 }}><Navbar /></div>

        {/* Two-column layout — stacks to one column under 900px */}
        <div className="lumora-hero-row" style={{
          position: 'relative',
          zIndex: 20,
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          pointerEvents: 'none',
        }}>
          {/* Left: branding */}
          <div className="lumora-hero-brand" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '0 64px',
            gap: 24,
            width: '100%',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #7C3AED, #6D28D9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                fontWeight: 800,
                color: '#fff',
                boxShadow: '0 0 32px rgba(124,58,237,0.4)',
              }}>L</div>
              <span style={{ fontSize: 26, fontWeight: 700, color: '#fff', letterSpacing: '-0.5px' }}>Lumora</span>
            </div>

            <h1 style={{ margin: 0, fontSize: 50, fontWeight: 700, lineHeight: 1.15, letterSpacing: '-1px', maxWidth: 460 }}>
              <TextType
                text={[
                  'From Conversation\nto Creation.',
                  'Build. Decide.\nShip Together.',
                  'Your Team\'s\nAI Workspace.',
                ]}
                typingSpeed={55}
                deletingSpeed={35}
                pauseDuration={2200}
                showCursor
                cursorCharacter="_"
                cursorBlinkDuration={0.5}
                loop
                style={{ color: '#f7f5fb' }}
              />
            </h1>

            <p style={{ margin: 0, fontSize: 15, color: 'rgba(229, 224, 250, 0.7)', maxWidth: 380, lineHeight: 1.7 }}>
              Collaborate. Create. Launch — the AI workspace built for your team.
            </p>


          </div>

          {/* Right: sign-in card */}
          <div className="lumora-hero-card" style={{
            width: 420,
            flexShrink: 0,
            marginRight: 80,
            background: 'rgba(26,22,37,0.88)',
            backdropFilter: 'blur(28px)',
            border: '1px solid rgba(124,58,237,0.22)',
            borderRadius: 18,
            padding: '40px 36px',
            boxShadow: '0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(124,58,237,0.06)',
            pointerEvents: 'auto',
          }}>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 30, fontWeight: 700, color: '#E8E0F5', letterSpacing: '-0.5px' }}>
                Lumora
              </h2>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#7C3AED',
                  textDecoration: 'underline',
                  textDecorationColor: 'rgba(124,58,237,0.4)',
                  cursor: 'default',
                }}>
                  Sign in
                </span>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'rgba(157,142,196,0.6)' }}>
                To Continue to Lumora
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label htmlFor="email" style={{ fontSize: 12, fontWeight: 500, color: 'rgba(157,142,196,0.7)', letterSpacing: '0.02em' }}>
                  Email Or Phone
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(124,58,237,0.28)',
                    borderRadius: 9,
                    color: '#E8E0F5',
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(124,58,237,0.7)' }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(124,58,237,0.28)' }}
                />
              </div>

              {error && (
                <p role="alert" style={{
                  margin: 0,
                  padding: '10px 14px',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#fca5a5',
                }}>
                  {error}
                </p>
              )}

              {/* T&C prompt — shown once email is typed */}
              {email && !termsAccepted && (
                <div style={{
                  padding: '10px 14px',
                  background: 'rgba(124,58,237,0.06)',
                  border: '1px solid rgba(124,58,237,0.2)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <span style={{ fontSize: 12, color: 'rgba(196,181,253,0.7)' }}>Accept the{' '}</span>
                  <span
                    onClick={() => setShowTermsModal(true)}
                    style={{
                      fontSize: 12,
                      color: '#7C3AED',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationColor: 'rgba(124,58,237,0.5)',
                      fontWeight: 600,
                    }}
                  >
                    Terms &amp; Conditions
                  </span>
                  <span style={{ fontSize: 12, color: 'rgba(196,181,253,0.7)' }}>{' '}to proceed.</span>
                </div>
              )}

              {/* Accepted confirmation */}
              {termsAccepted && (
                <div style={{
                  padding: '10px 14px',
                  background: 'rgba(34,197,94,0.07)',
                  border: '1px solid rgba(34,197,94,0.22)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <span style={{ fontSize: 14, color: '#4ade80' }}>✓</span>
                  <span style={{ fontSize: 12, color: 'rgba(134,239,172,0.8)' }}>
                    Terms &amp; Conditions accepted.
                  </span>
                  <span
                    onClick={() => { setTermsAccepted(false); setTermsChecked(false) }}
                    style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(157,142,196,0.45)', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    revoke
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  type="submit"
                  disabled={loading || !email || !termsAccepted}
                  style={{
                    padding: '9px 28px',
                    background: 'transparent',
                    border: `1px solid ${loading || !email || !termsAccepted ? 'rgba(124,58,237,0.25)' : 'rgba(124,58,237,0.7)'}`,
                    borderRadius: 999,
                    color: loading || !email || !termsAccepted ? 'rgba(157,142,196,0.35)' : '#C4B5FD',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: loading || !email || !termsAccepted ? 'not-allowed' : 'pointer',
                    backdropFilter: 'blur(8px)',
                    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                    letterSpacing: '0.02em',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && email && termsAccepted) {
                      e.currentTarget.style.background = 'rgba(124,58,237,0.15)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  {loading ? 'Signing in…' : 'Next'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── Terms & Conditions Modal ── */}
      {showTermsModal && (
        <div
          onClick={() => setShowTermsModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(7,5,14,0.82)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 560,
              background: 'rgba(22,18,36,0.97)',
              border: '1px solid rgba(124,58,237,0.28)',
              borderRadius: 18,
              boxShadow: '0 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(124,58,237,0.08)',
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '24px 28px 20px',
              borderBottom: '1px solid rgba(124,58,237,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#E8E0F5', letterSpacing: '-0.3px' }}>
                  Terms &amp; Conditions
                </h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: 'rgba(157,142,196,0.5)' }}>
                  Lumora Privacy Policy &amp; Terms of Service
                </p>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(157,142,196,0.5)', fontSize: 20, lineHeight: 1,
                  padding: 4, borderRadius: 6,
                }}
              >✕</button>
            </div>

            {/* Scrollable body */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '20px 28px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(124,58,237,0.3) transparent',
            }}>
              {[
                {
                  title: '1. Acceptance of Terms',
                  body: 'By accessing or using Lumora, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our services.',
                },
                {
                  title: '2. Privacy & Data Collection',
                  body: 'We collect information you provide directly to us, including your email address, usage data, and conversation history. This data is used solely to provide and improve the Lumora service. We do not sell your personal data to third parties.',
                },
                {
                  title: '3. Use of AI Services',
                  body: 'Lumora uses large language models to generate responses. AI-generated content may be inaccurate or incomplete. You are responsible for verifying any information before acting on it. Do not share sensitive personal, financial, or medical information with the AI.',
                },
                {
                  title: '4. User Responsibilities',
                  body: 'You agree not to use Lumora for any unlawful purpose, to generate harmful or misleading content, to attempt to reverse-engineer or interfere with our systems, or to impersonate any person or entity.',
                },
                {
                  title: '5. Intellectual Property',
                  body: 'All content, trademarks, and technology on Lumora are the property of Illimite Inc. or its licensors. You retain ownership of content you create, but grant Lumora a limited license to process it to deliver the service.',
                },
                {
                  title: '6. Data Retention & Deletion',
                  body: 'We retain your data for as long as your account is active. You may request deletion of your account and associated data at any time by contacting support. Some data may be retained for legal compliance purposes.',
                },
                {
                  title: '7. Limitation of Liability',
                  body: 'Lumora is provided "as is" without warranties of any kind. To the maximum extent permitted by law, Illimite Inc. shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service.',
                },
                {
                  title: '8. Changes to Terms',
                  body: 'We may update these Terms from time to time. We will notify you of significant changes via email or in-app notification. Continued use of Lumora after changes constitutes acceptance of the updated Terms.',
                },
              ].map(({ title, body }) => (
                <div key={title} style={{ marginBottom: 20 }}>
                  <h3 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#C4B5FD' }}>{title}</h3>
                  <p style={{ margin: 0, fontSize: 13, color: 'rgba(196,181,253,0.55)', lineHeight: 1.75 }}>{body}</p>
                </div>
              ))}
            </div>

            {/* Footer — checkbox + button */}
            <div style={{
              padding: '18px 28px 22px',
              borderTop: '1px solid rgba(124,58,237,0.15)',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                  style={{ marginTop: 2, accentColor: '#7C3AED', width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, color: 'rgba(196,181,253,0.7)', lineHeight: 1.6 }}>
                  I have read and agree to Lumora&apos;s{' '}
                  <span style={{ color: '#7C3AED' }}>Terms of Service</span>{' '}
                  and{' '}
                  <span style={{ color: '#7C3AED' }}>Privacy Policy</span>.
                </span>
              </label>

              <button
                disabled={!termsChecked}
                onClick={() => { setTermsAccepted(true); setShowTermsModal(false) }}
                style={{
                  padding: '11px 0',
                  background: termsChecked ? 'linear-gradient(135deg, #7C3AED, #6D28D9)' : 'rgba(124,58,237,0.1)',
                  border: `1px solid ${termsChecked ? 'transparent' : 'rgba(124,58,237,0.2)'}`,
                  borderRadius: 10,
                  color: termsChecked ? '#fff' : 'rgba(157,142,196,0.3)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: termsChecked ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s, color 0.2s',
                  letterSpacing: '0.02em',
                  boxShadow: termsChecked ? '0 0 24px rgba(124,58,237,0.35)' : 'none',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <LandingAnimations />

      {/* ── Below-fold landing sections ── */}
      <div id="features"><FeaturedModels /></div>
      <LogoLoop />
      <AiTrendsChart />
      <div id="workflow"><TeamSection /></div>
      <div id="testimonials"><Testimonials /></div>
      <div id="faqs"><FAQ /></div>
      <LandingFooter />

    </div>
  )
}
